import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import EnglishQuizForm from "./components/EnglishQuizForm";

import {
  useCreateEnglishQuizMutation,
} from "../../slices/englishQuizApiSlice";

import {
  useGetQuestionBankSubjectsQuery,
} from "../../slices/questionBankSubjectApiSlice";

/**
 * EnglishQuizCreate
 *
 * Page for creating a new English quiz.
 */
const EnglishQuizCreate = () => {
  const navigate = useNavigate();

  // ======================================================
  // Create Quiz Mutation
  // ======================================================

  const [
    createEnglishQuiz,
    {
      isLoading: isCreating,
      error: createError,
    },
  ] = useCreateEnglishQuizMutation();

  // ======================================================
  // Question Bank Subjects
  // ======================================================

  const {
    data: subjectsData,
    isLoading: isSubjectsLoading,
    isError: isSubjectsError,
    error: subjectsError,
  } = useGetQuestionBankSubjectsQuery();

  // ======================================================
  // Normalize Subjects Response
  // ======================================================

  const subjects = useMemo(() => {
    if (Array.isArray(subjectsData)) {
      return subjectsData;
    }

    if (Array.isArray(subjectsData?.subjects)) {
      return subjectsData.subjects;
    }

    if (Array.isArray(subjectsData?.data)) {
      return subjectsData.data;
    }

    return [];
  }, [subjectsData]);

  // ======================================================
  // Submit
  // ======================================================

  const handleSubmit = async (formData) => {
    try {
      const response = await createEnglishQuiz(
        formData,
      ).unwrap();

      const createdQuiz =
        response?.quiz ||
        response?.data ||
        response;

      const quizId =
        createdQuiz?._id ||
        createdQuiz?.id;

      if (quizId) {
        // Navigate to the created quiz
        navigate(
          `/english-quizzes/${quizId}`,
          {
            replace: true,
          },
        );

        return;
      }

      // Fallback
      navigate("/english-quizzes", {
        replace: true,
      });
    } catch (error) {
      console.error(
        "Failed to create English quiz:",
        error,
      );
    }
  };

  // ======================================================
  // Cancel
  // ======================================================

  const handleCancel = () => {
    navigate("/english-quizzes");
  };

  // ======================================================
  // Back to List
  // ======================================================

  const handleBackToList = () => {
    navigate("/english-quizzes");
  };

  // ======================================================
  // Error Message
  // ======================================================

  const errorMessage =
    typeof createError === "string"
      ? createError
      : createError?.data?.message ||
        createError?.message ||
        null;

  const subjectErrorMessage =
    typeof subjectsError === "string"
      ? subjectsError
      : subjectsError?.data?.message ||
        subjectsError?.message ||
        "Unable to load question bank subjects.";

  // ======================================================
  // Render
  // ======================================================

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ==================================================
          Page Header
      ================================================== */}

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

            {/* Left */}

            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500">

                <button
                  type="button"
                  onClick={handleBackToList}
                  className="transition hover:text-indigo-600"
                >
                  English Quizzes
                </button>

                <span>/</span>

                <span className="text-slate-700">
                  Create
                </span>
              </div>

              <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Create English Quiz
              </h1>

              <p className="mt-1 max-w-2xl text-sm text-slate-500">
                Create and configure a new English quiz
                for your students.
              </p>
            </div>

            {/* Cancel */}

            <button
              type="button"
              onClick={handleCancel}
              disabled={isCreating}
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>

          </div>
        </div>
      </div>

      {/* ==================================================
          Content
      ================================================== */}

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">

        {/* ==================================================
            Create API Error
        ================================================== */}

        {errorMessage && (
          <div
            role="alert"
            className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4"
          >
            <div className="flex gap-3">

              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                  />

                  <path
                    strokeLinecap="round"
                    d="M12 8v5"
                  />

                  <path
                    strokeLinecap="round"
                    d="M12 16h.01"
                  />
                </svg>
              </div>

              <div>
                <p className="text-sm font-semibold text-red-900">
                  Unable to create English quiz
                </p>

                <p className="mt-1 text-sm text-red-700">
                  {errorMessage}
                </p>
              </div>

            </div>
          </div>
        )}

        {/* ==================================================
            Subjects Error
        ================================================== */}

        {isSubjectsError && (
          <div
            role="alert"
            className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4"
          >
            <p className="text-sm font-semibold text-red-900">
              Unable to load subjects
            </p>

            <p className="mt-1 text-sm text-red-700">
              {subjectErrorMessage}
            </p>
          </div>
        )}

        {/* ==================================================
            Form
        ================================================== */}

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">

          <EnglishQuizForm
            mode="create"
            subjects={subjects}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={
              isCreating || isSubjectsLoading
            }
            submitLabel="Create Quiz"
            error={errorMessage}
          />

        </div>
      </main>
    </div>
  );
};

export default EnglishQuizCreate;